'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentProps } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { FormAddressAutocompleteField } from './form-address-autocomplete-field';
import { FormAmountCheckbox } from './form-amount-checkbox';
import { FormAmountInput } from './form-amount-input';
import { FormAmountRadio } from './form-amount-radio';
import { FormCheckbox } from './form-checkbox';
import { FormDateField } from './form-date-field';
import { FormDropdown } from './form-dropdown';
import { FormMultiSelect } from './form-multi-select';
import { FormPasswordField } from './form-password-field';
import { FormRadioGroup } from './form-radio-group';
import { FormSearchBar } from './form-search-bar';
import { FormSelect } from './form-select';
import { FormSlider } from './form-slider';
import { FormStarRating } from './form-star-rating';
import { FormSwitch } from './form-switch';
import { FormTabsSelector } from './form-tabs-selector';
import { FormTelephoneField } from './form-telephone-field';
import { FormTextField } from './form-text-field';
import { FormTextareaField } from './form-textarea-field';

type Props<T extends Record<string, any>> = UseFormReturn<T> & ComponentProps<'form'>;
const Root = <T extends Record<string, any>>({
    clearErrors,
    control,
    formState,
    getFieldState,
    getValues,
    handleSubmit,
    register,
    reset,
    resetField,
    setError,
    setFocus,
    setValue,
    subscribe,
    trigger,
    unregister,
    watch,
    ...props
}: Props<T>) => (
    <FormProvider
        {...{
            clearErrors,
            control,
            formState,
            getFieldState,
            getValues,
            handleSubmit,
            register,
            reset,
            resetField,
            setError,
            setFocus,
            setValue,
            subscribe,
            trigger,
            unregister,
            watch,
        }}
    >
        <form {...props} />
    </FormProvider>
);

export const Form: typeof Root & {
    SearchBar: typeof FormSearchBar;
    Dropdown: typeof FormDropdown;
    Select: typeof FormSelect;
    TextField: typeof FormTextField;
    DateField: typeof FormDateField;
    Checkbox: typeof FormCheckbox;
    PasswordField: typeof FormPasswordField;
    AddressAutocompleteField: typeof FormAddressAutocompleteField;
    MultiSelect: typeof FormMultiSelect;
    TextArea: typeof FormTextareaField;
    StarRating: typeof FormStarRating;
    RadioGroup: typeof FormRadioGroup;
    AmountInput: typeof FormAmountInput;
    AmountCheckbox: typeof FormAmountCheckbox;
    AmountRadio: typeof FormAmountRadio;
    Slider: typeof FormSlider;
    Switch: typeof FormSwitch;
    TabsSelector: typeof FormTabsSelector;
    TelephoneField: typeof FormTelephoneField;
} = Object.assign(Root, {
    SearchBar: FormSearchBar,
    Dropdown: FormDropdown,
    Select: FormSelect,
    TextField: FormTextField,
    DateField: FormDateField,
    Checkbox: FormCheckbox,
    PasswordField: FormPasswordField,
    AddressAutocompleteField: FormAddressAutocompleteField,
    MultiSelect: FormMultiSelect,
    TextArea: FormTextareaField,
    StarRating: FormStarRating,
    RadioGroup: FormRadioGroup,
    AmountInput: FormAmountInput,
    AmountCheckbox: FormAmountCheckbox,
    AmountRadio: FormAmountRadio,
    Slider: FormSlider,
    Switch: FormSwitch,
    TabsSelector: FormTabsSelector,
    TelephoneField: FormTelephoneField,
});
